import { ImageUploadService } from './imageUploadService';
import { VideoUploadService } from './videoUploadService';
import { MediaType, MediaFile } from '../types';

/**
 * Service unifié pour gérer les médias (images et vidéos)
 */
export class MediaService {
  
  /**
   * Détermine le type de média à partir d'un fichier
   * @param file - Le fichier à analyser
   * @returns MediaType - Le type de média
   */
  static getMediaType(file: File): MediaType {
    if (file.type.startsWith('image/')) {
      return file.type === 'image/gif' ? 'gif' : 'image';
    }
    if (file.type.startsWith('video/')) {
      return 'video';
    }
    throw new Error('Type de fichier non supporté');
  }

  /**
   * Valide un fichier média
   * @param file - Le fichier à valider
   * @returns Promise<{ isValid: boolean; error?: string; duration?: number }>
   */
  static async validateMediaFile(file: File): Promise<{ isValid: boolean; error?: string; duration?: number }> {
    try {
      const mediaType = this.getMediaType(file);
      
      switch (mediaType) {
        case 'image':
        case 'gif':
          const imageValidation = ImageUploadService.validateImageFile(file);
          return imageValidation;
          
        case 'video':
          return await VideoUploadService.validateVideoFile(file);
          
        default:
          return { isValid: false, error: 'Type de fichier non supporté' };
      }
    } catch (error) {
      return { isValid: false, error: 'Type de fichier non supporté' };
    }
  }

  /**
   * Upload un fichier média vers Firebase Storage
   * @param file - Le fichier à uploader
   * @param path - Le chemin de destination
   * @returns Promise<string> - L'URL du fichier uploadé
   */
  static async uploadMediaFile(file: File, path: string): Promise<string> {
    const mediaType = this.getMediaType(file);
    
    switch (mediaType) {
      case 'image':
      case 'gif':
        return await ImageUploadService.uploadImage(file, path);
        
      case 'video':
        return await VideoUploadService.uploadVideo(file, path);
        
      default:
        throw new Error('Type de fichier non supporté');
    }
  }

  /**
   * Upload multiple fichiers médias pour un post
   * @param files - Array des fichiers à uploader
   * @returns Promise<{ urls: string[]; types: MediaType[] }>
   */
  static async uploadPostMedia(files: File[]): Promise<{ urls: string[]; types: MediaType[] }> {
    if (files.length === 0) return { urls: [], types: [] };
    
    // Vérifications des règles de mix de médias
    const mediaTypes = files.map(file => this.getMediaType(file));
    const hasVideo = mediaTypes.includes('video');
    const hasImageOrGif = mediaTypes.some(type => type === 'image' || type === 'gif');
    
    // Règle : pas de mix vidéo + image/gif
    if (hasVideo && hasImageOrGif) {
      throw new Error('Vous ne pouvez pas mélanger vidéos et images dans un même post');
    }
    
    // Règle : maximum 1 vidéo par post
    const videoCount = mediaTypes.filter(type => type === 'video').length;
    if (videoCount > 1) {
      throw new Error('Maximum 1 vidéo autorisée par post');
    }
    
    // Règle : maximum 4 images/gifs par post
    const imageCount = mediaTypes.filter(type => type === 'image' || type === 'gif').length;
    if (imageCount > 4) {
      throw new Error('Maximum 4 images autorisées par post');
    }
    
    console.log(`🚀 Upload de ${files.length} fichier(s) média pour un post...`);
    
    // Upload tous les fichiers
    const uploadPromises = files.map(file => this.uploadMediaFile(file, 'posts'));
    const urls = await Promise.all(uploadPromises);
    
    console.log(`✅ ${urls.length} fichier(s) média uploadé(s) avec succès`);
    return { urls, types: mediaTypes };
  }

  /**
   * Crée un objet MediaFile à partir d'un fichier
   * @param file - Le fichier source
   * @returns Promise<MediaFile>
   */
  static async createMediaFile(file: File): Promise<MediaFile> {
    const type = this.getMediaType(file);
    const url = URL.createObjectURL(file);
    
    const mediaFile: MediaFile = {
      file,
      type,
      url
    };
    
    // Pour les vidéos, ajouter durée et miniature
    if (type === 'video') {
      const metadata = await VideoUploadService.getVideoMetadata(file);
      if (metadata) {
        mediaFile.duration = metadata.duration;
      }
      
      const thumbnailBlob = await VideoUploadService.generateThumbnail(file);
      if (thumbnailBlob) {
        mediaFile.thumbnail = URL.createObjectURL(thumbnailBlob);
      }
    }
    
    return mediaFile;
  }

  /**
   * Nettoie les URLs de prévisualisation créées avec createObjectURL
   * @param mediaFiles - Array des fichiers média à nettoyer
   */
  static cleanupMediaFiles(mediaFiles: MediaFile[]) {
    mediaFiles.forEach(mediaFile => {
      if (mediaFile.url) {
        URL.revokeObjectURL(mediaFile.url);
      }
      if (mediaFile.thumbnail) {
        URL.revokeObjectURL(mediaFile.thumbnail);
      }
    });
  }

  /**
   * Détermine si un URL est une vidéo
   * @param url - L'URL à vérifier
   * @returns boolean
   */
  static isVideoUrl(url: string): boolean {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext)) || 
           lowerUrl.includes('video') ||
           lowerUrl.includes('storage.googleapis.com') && lowerUrl.includes('video');
  }

  /**
   * Détermine si un URL est un GIF
   * @param url - L'URL à vérifier
   * @returns boolean
   */
  static isGifUrl(url: string): boolean {
    return url.toLowerCase().includes('.gif') || 
           url.toLowerCase().includes('giphy') || 
           url.toLowerCase().includes('tenor');
  }

  /**
   * Obtient le type de média à partir d'une URL
   * @param url - L'URL à analyser
   * @returns MediaType
   */
  static getMediaTypeFromUrl(url: string): MediaType {
    if (this.isVideoUrl(url)) return 'video';
    if (this.isGifUrl(url)) return 'gif';
    return 'image';
  }

  /**
   * Formate la durée en format lisible (mm:ss)
   * @param seconds - Durée en secondes
   * @returns string - Durée formatée
   */
  static formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Obtient la taille formatée d'un fichier
   * @param bytes - Taille en bytes
   * @returns string - Taille formatée
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
